import React, { useContext, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { dbPost, dbForm } from '../../apis/commonApi';
import TextEditor from '../../components/inputComponents/TextEditor';
import TextViewer from '../../components/inputComponents/TextViewer';
import FileUpload from '../../components/modalComponents/FileUpload';
import GbitTablePaging from '../../components/tableComponents/GbitTablePaging';
import { GlobalContext } from '../../components/contexts';
import { nlPost } from '../../apis/notLoginApi';

const NoticeList = () => {
  useEffect(() => {
    document.title = '공지 목록';
  }, []);

  const { store } = useContext(GlobalContext);
  const location = useLocation();

  // State 관리
  const [notices, setNotices] = useState([]);
  const [isWriting, setIsWriting] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(
    location.state?.selectedNotice || null
  );
  const [title, setTitle] = useState(selectedNotice?.qstnTtl || '');
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // 파일 관련 상태
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [sharedFileId, setSharedFileId] = useState(null);
  const [currentFileSeq, setCurrentFileSeq] = useState(null);

  const editorRef = useRef(null);

  const fetchNotices = async () => {
    try {
      const url = '/cs/ask/search.do';
      const filters = { ctgryId: 'NOTICE' };
      const response = await nlPost(url, filters);
      const sortedNotices = Array.isArray(response)
        ? response.sort((a, b) => new Date(b.regDt) - new Date(a.regDt))
        : [];
      setNotices(sortedNotices);
    } catch (error) {
      setNotices([]);
    }
  };

  useEffect(() => {
    fetchNotices();
    return () => {
      if (editorRef.current) {
        editorRef.current.getInstance().destroy();
      }
    };
  }, []);

  useEffect(() => {
    if (selectedNotice) {
      if (editorRef.current) {
        editorRef.current
          .getInstance()
          .setMarkdown(selectedNotice.qstnCn || '');
      }
    }
  }, [selectedNotice]);

  const handleNoticeClick = notice => {
    setSelectedNotice(notice);
    setTitle(notice.qstnTtl);
    if (editorRef.current) {
      editorRef.current.getInstance().setMarkdown(notice.qstnCn || '');
    }
  };

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const paginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return notices.slice(startIndex, startIndex + itemsPerPage);
  };

  const resetFormState = () => {
    setTitle('');
    if (editorRef.current) {
      editorRef.current.getInstance().setMarkdown('');
    }
    setSharedFileId(null);
    setUploadedFiles([]);
    setSelectedFiles([]);
  };

  const handleRegister = async () => {
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    if (title.length > 33) {
      alert('제목은 33글자를 초과할 수 없습니다.');
      return;
    }
    const content = editorRef.current.getInstance().getMarkdown();
    if (!content.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }
    if (content.length > 1000) {
      alert('내용은 1000글자를 초과할 수 없습니다.');
      return;
    }

    const payload = {
      qstnTtl: title,
      qstnCn: content,
      entSn: store.entSn || '',
      ctgryId: 'NOTICE',
      fileId: sharedFileId,
    };

    try {
      const url = '/cs/ask/create.do';
      await dbPost(url, payload);
      alert('공지사항이 성공적으로 등록되었습니다.');
      resetFormState();
      setIsWriting(false);
      fetchNotices();
    } catch (error) {
      alert('공지사항 등록 중 문제가 발생했습니다.');
    }
  };

  const handleUpdate = async () => {
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    const content = editorRef.current.getInstance().getMarkdown();
    if (!content.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }

    const payload = {
      qstnId: selectedNotice.qstnId,
      qstnTtl: title,
      qstnCn: content,
      fileId: sharedFileId,
    };

    try {
      const url = '/cs/ask/update.do';
      await dbPost(url, payload);
      alert('공지사항이 성공적으로 수정되었습니다.');
      resetFormState();
      setSelectedNotice(null);
      fetchNotices();
    } catch (error) {
      alert('공지사항 수정 중 문제가 발생했습니다.');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        const url = '/cs/ask/delete.do';
        await dbPost(url, { qstnId: selectedNotice.qstnId });
        alert('공지사항이 성공적으로 삭제되었습니다.');
        resetFormState();
        setSelectedNotice(null);
        fetchNotices();
      } catch (error) {
        alert('공지사항 삭제 중 문제가 발생했습니다.');
      }
    }
  };

  const handleCancelWriting = () => {
    resetFormState();
    setIsWriting(false);
    setSelectedNotice(null);
  };

  const handleFileUpload = uploadedFilePath => {
    const cleanPath = uploadedFilePath.replace(/^.*[\\/]/, '');
    const uploadedUrl = `${process.env.REACT_APP_BACKEND_URL}/files/${cleanPath}`;
    if (editorRef.current) {
      const editorInstance = editorRef.current.getInstance();
      const currentContent = editorInstance.getMarkdown();
      const imageMarkdown = `![이미지 설명](${uploadedUrl})`;
      editorInstance.setMarkdown(`${currentContent}\n${imageMarkdown}`);
    }
  };

  const renderList = () => (
    <div className="contents">
      <div className="page-title-wrap" data-type="responsive">
        <h2 className="h-tit">공지사항</h2>
      </div>
      <ul className="guide-txt">
        <li>중요한 공지사항을 읽어주세요.</li>
      </ul>
      <div className="tbl-wrap">
        <table className="tbl col data scroll">
          <caption>공지사항 목록</caption>
          <colgroup>
            <col style={{ width: '10%' }} />
            <col style={{ width: '50%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '20%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>No</th>
              <th>제목</th>
              <th>글쓴이</th>
              <th>작성 시간</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData().map((notice, index) => (
              <tr
                key={notice.qstnId}
                onClick={() => handleNoticeClick(notice)}
                className="clickable-row"
              >
                <td>{index + 1}</td>
                <td>{notice.qstnTtl}</td>
                <td>{notice.userNm}</td>
                <td>{notice.regDt.split(' ')[0]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="page-btn-wrap">
        <GbitTablePaging
          totalItems={notices.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
        {store.authrtId === 'ADMIN' && (
          <button
            type="button"
            className="btn xlg primary"
            onClick={() => {
              setIsWriting(true);
              setSelectedNotice(null);
              setTitle('');
              if (editorRef.current) {
                editorRef.current.getInstance().setMarkdown('');
              }
            }}
          >
            등록하기
          </button>
        )}
      </div>
    </div>
  );

  const renderForm = () => (
    <div className="contents">
      <div className="page-title-wrap" data-type="responsive">
        <h2 className="h-tit">공지사항 {selectedNotice ? '수정' : '등록'}</h2>
      </div>
      <div className="tbl-wrap">
        <table className="tbl data row">
          <tbody>
            <tr>
              <th>{selectedNotice ? '수정 제목' : '등록 제목'}</th>
              <td>
                <label className="hidden-label" htmlFor="title">
                  라벨
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="제목을 입력해주세요."
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  aria-labelledby="title"
                  id="title"
                />
              </td>
            </tr>
            <tr>
              <th>{selectedNotice ? '수정 내용' : '등록 내용'}</th>
              <td>
                <label className="hidden-label" htmlFor="content">
                  라벨
                </label>
                <TextEditor
                  className="form-control"
                  aria-labelledby="content"
                  ref={editorRef}
                  initialValue={selectedNotice ? selectedNotice.qstnCn : ''}
                  id="content"
                />
              </td>
            </tr>
            <tr>
              <th>첨부파일</th>
              <td>
                <div className="input-group">
                  <button
                    type="button"
                    className="btn tertiary"
                    onClick={() => setIsFileModalOpen(true)}
                  >
                    파일 첨부
                  </button>
                </div>
                {uploadedFiles.length > 0 && (
                  <p>첨부된 파일 개수: {uploadedFiles.length}개</p>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="page-btn-wrap">
        <button
          type="button"
          className="btn xlg secondary"
          style={{ gap: '1rem' }}
          onClick={() => {
            handleCancelWriting();
          }}
        >
          돌아가기
        </button>
        {selectedNotice ? (
          <>
            <button
              type="button"
              className="btn xlg tertiary"
              onClick={handleDelete}
            >
              삭제
            </button>
            <button
              type="button"
              className="btn xlg tertiary"
              onClick={handleUpdate}
            >
              수정
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn xlg primary"
            onClick={handleRegister}
          >
            등록
          </button>
        )}
      </div>
    </div>
  );

  const renderReadOnlyView = () => (
    <div className="contents">
      <div className="page-title-wrap" data-type="responsive">
        <h2 className="h-tit">공지사항 조회</h2>
      </div>
      <div className="tbl-wrap">
        <table className="tbl data row">
          <tbody>
            <tr>
              <th>제목</th>
              <td>{selectedNotice.qstnTtl}</td>
            </tr>
            <tr>
              <th>내용</th>
              <td>
                <TextViewer
                  content={selectedNotice.qstnCn || '내용이 없습니다.'}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="page-btn-wrap">
        <button
          type="button"
          className="btn xlg tertiary"
          onClick={() => setSelectedNotice(null)}
        >
          돌아가기
        </button>
      </div>
    </div>
  );

  return (
    <div className="inner in-between">
      {store.authrtId === 'ADMIN'
        ? isWriting || selectedNotice
          ? renderForm()
          : renderList()
        : selectedNotice
        ? renderReadOnlyView()
        : renderList()}
      {isFileModalOpen && (
        <FileUpload
          onClose={() => setIsFileModalOpen(false)}
          dbPost={dbPost}
          dbForm={dbForm}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          sharedFileId={sharedFileId}
          setSharedFileId={setSharedFileId}
          currentFileSeq={currentFileSeq}
          setCurrentFileSeq={setCurrentFileSeq}
          onUpload={handleFileUpload}
        />
      )}
    </div>
  );
};

export default NoticeList;
